import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCw, X } from '@/components/icons';
import { applyServiceWorkerUpdate } from '@/pwa/register-service-worker';

/**
 * `<PwaUpdateBanner />` — canonical surface per
 * `sustevo-handbook/brand/APP_SHELL.md` § 11.1 (contract 1.2.0).
 *
 * Bottom-right card that surfaces when the SW activates a new build. Two
 * channels: explicit `BroadcastChannel('sustevo-sw')` `{ type: 'NEW_VERSION',
 * buildId }` messages from the SW + a `navigator.serviceWorker
 * controllerchange` fallback for SWs that don't broadcast. Same banner can't
 * fire twice for the same `buildId` within a session.
 *
 * Auto-reload safety (§ 11.1.3) is the load-bearing part of this rewrite —
 * previously the banner auto-applied on `controllerchange` and silently
 * discarded any unsaved form input. Four-layer gate:
 *   1. `<body data-pwa-auto-reload="true">` opt-in wins.
 *   2. `<body data-pwa-no-auto-reload="true">` refuses.
 *   3. Heuristic — non-empty `<input>` / `<textarea>` or `aria-dirty="true"`
 *      refuses. Skips `submit`/`button`/`reset`/`hidden`/`checkbox`.
 *   4. Per-element `data-pwa-ignore="true"` exempts noisy widgets from (3).
 *
 * When the gate refuses, the banner renders and the user picks: Refresh now
 * (hard reload) or Later (dismiss for this buildId, session-scoped).
 *
 * i18n: spec § 11.1.5 canonical keys are `pwa.update.*` + `common.close`;
 * this implementation still ships the legacy `system.pwaUpdate.*` namespace.
 * Per § 11.1.5 existing apps "may migrate at next i18n cleanup" — deferred.
 */

const BROADCAST_CHANNEL_NAME = 'sustevo-sw';
const DISMISSED_STORAGE_KEY = 'senviro.pwa-update.dismissed';

interface NewVersionMessage {
  type: 'NEW_VERSION';
  buildId?: string;
}

function isNewVersionMessage(data: unknown): data is NewVersionMessage {
  if (typeof data !== 'object' || data === null) return false;
  const m = data as { type?: unknown };
  return m.type === 'NEW_VERSION';
}

function readDismissed(): string | null {
  try {
    return typeof window === 'undefined'
      ? null
      : window.sessionStorage.getItem(DISMISSED_STORAGE_KEY);
  } catch {
    return null;
  }
}

function writeDismissed(buildId: string): void {
  try {
    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(DISMISSED_STORAGE_KEY, buildId);
    }
  } catch {
    // sessionStorage unavailable (Safari private mode); silent per § 11.1.4.
  }
}

/**
 * Heuristic for layer (3) — scans visible inputs/textareas for unsaved
 * content. Skips input types where the value is URL-controlled, inert, or
 * trivial to recreate. Exported for unit-test reach.
 */
export function hasUnsavedInput(): boolean {
  if (typeof document === 'undefined') return false;
  const skipTypes = new Set(['submit', 'button', 'reset', 'hidden', 'checkbox']);
  const nodes = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
    'input, textarea',
  );
  for (const el of nodes) {
    if (el.dataset.pwaIgnore === 'true') continue;
    if (el instanceof HTMLInputElement && skipTypes.has(el.type)) continue;
    if (el.getAttribute('aria-dirty') === 'true') return true;
    if (el.value && el.value.length > 0) return true;
  }
  return false;
}

/**
 * Four-layer gate per § 11.1.3. Returns true when the banner MUST stay
 * visible (i.e. auto-reload would discard the user's work). Exported for
 * unit-test reach.
 */
export function isAutoReloadDisallowed(): boolean {
  if (typeof document === 'undefined') return false;
  const body = document.body;
  if (body?.dataset.pwaAutoReload === 'true') return false; // (1) opt-in
  if (body?.dataset.pwaNoAutoReload === 'true') return true; // (2) opt-out
  return hasUnsavedInput(); // (3) heuristic with (4) per-element opt-outs
}

export function PwaUpdateBanner(): JSX.Element | null {
  const { t } = useTranslation();
  const [availableBuildId, setAvailableBuildId] = useState<string | null>(null);
  // § 11.1.1 — Set<buildId> prevents the same activation from surfacing the
  // banner more than once per session (the ref survives re-renders).
  const seenBuildIds = useRef<Set<string>>(new Set());

  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

    const announce = (buildId: string): void => {
      if (seenBuildIds.current.has(buildId)) return;
      seenBuildIds.current.add(buildId);
      if (readDismissed() === buildId) return;
      // § 11.1.3 — auto-reload silently when nothing on the page would be
      // discarded. If anything refuses, render the banner instead.
      if (!isAutoReloadDisallowed()) {
        // Tiny delay so React commits any in-flight state before reload.
        window.setTimeout(() => {
          window.location.reload();
        }, 50);
        return;
      }
      setAvailableBuildId(buildId);
    };

    let channel: BroadcastChannel | undefined;
    const onMessage = (event: MessageEvent): void => {
      if (!isNewVersionMessage(event.data)) return;
      announce(event.data.buildId ?? 'unknown');
    };
    const onControllerChange = (): void => {
      announce('controllerchange');
    };

    try {
      channel = new BroadcastChannel(BROADCAST_CHANNEL_NAME);
      channel.addEventListener('message', onMessage);
    } catch {
      // BroadcastChannel unsupported (old Safari); controllerchange covers it.
    }
    navigator.serviceWorker.addEventListener('controllerchange', onControllerChange);

    return () => {
      channel?.removeEventListener('message', onMessage);
      channel?.close();
      navigator.serviceWorker.removeEventListener('controllerchange', onControllerChange);
    };
  }, []);

  if (!availableBuildId) return null;

  const handleRefresh = (): void => {
    void applyServiceWorkerUpdate();
    // applyServiceWorkerUpdate posts SKIP_WAITING + reloads; banner unmounts
    // with the page.
  };

  const handleDismiss = (): void => {
    writeDismissed(availableBuildId);
    setAvailableBuildId(null);
  };

  return (
    <output
      aria-live="polite"
      data-testid="pwa-update-banner"
      className="fixed bottom-4 right-4 z-50 flex max-w-sm items-start gap-3 rounded-lg border border-[var(--color-blue-300)] bg-card p-4 shadow-lg"
    >
      <div className="flex-1 text-sm">
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-[var(--color-blue-600)]">
          {t('system.pwaUpdate.title', { defaultValue: 'Update available' })}
        </p>
        <p className="mt-1 text-foreground">
          {t('system.pwaUpdate.body', {
            defaultValue: 'A new version of Senviro is available.',
          })}
        </p>
        <div className="mt-3 flex items-center gap-2">
          <button
            type="button"
            onClick={handleRefresh}
            data-testid="pwa-update-refresh"
            className="inline-flex items-center gap-1 rounded-md border border-[var(--color-blue-600)] bg-[var(--color-blue-600)] px-3 py-1.5 text-xs font-medium text-white transition hover:bg-[var(--color-blue-700)]"
          >
            <RefreshCw size={12} weight="bold" aria-hidden="true" />
            {t('system.pwaUpdate.refresh', { defaultValue: 'Refresh now' })}
          </button>
          <button
            type="button"
            onClick={handleDismiss}
            data-testid="pwa-update-later"
            className="rounded-md border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-muted"
          >
            {t('system.pwaUpdate.later', { defaultValue: 'Later' })}
          </button>
        </div>
      </div>
      <button
        type="button"
        onClick={handleDismiss}
        aria-label={t('system.pwaUpdate.dismiss', { defaultValue: 'Close' })}
        data-testid="pwa-update-dismiss"
        className="rounded-md p-1 text-muted-foreground transition hover:bg-muted hover:text-foreground"
      >
        <X size={14} weight="regular" aria-hidden="true" />
      </button>
    </output>
  );
}
